import { Component, 
  OnInit, 
  OnDestroy,
  Output, 
  EventEmitter, 
  ChangeDetectorRef, 
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { catchError, finalize, forkJoin, of, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  F1065K2DataEntryService,
  ColumnHeaderData,
  LineData,
  GroupedFormLine,
  FormLineItem,
  SaveDataItem,
  ClobSettingsObj,
  FilterParams
} from '../f1065-k2-data-entry.service';

@Component({
    selector: 'gtw-f1065-k2-p3s4',
    templateUrl: './f1065-k2-p3s4.component.html',
    styleUrls: ['./f1065-k2-p3s4.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1065K2P3S4Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';
  private myScreenKey: any = null;
  private firstSetTabActiveSkipped = false;

  // ============================================
  // OUTPUT EVENTS
  // ============================================
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // ============================================
  // INPUT PROPERTIES (from AngularJS)
  // ============================================
  baseURLs: any = {};
  globalService: any = {};
  filterParams: FilterParams = {};
  GENERAL_CONFIG: any = {};

  // Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  // ============================================
  // COMPONENT STATE
  // ============================================
  
  // Loading states
  loadingData = true;
  showLoader = true;

  // Data
  colHeaderData: ColumnHeaderData[] = [];
  lineData: LineData[] = [];
  groupedFormLines: GroupedFormLine[] = [];

  // Dropdown loading tracking
  totalDropDownActions = 0;
  loadedActionCounter = 0;

  // Form state
  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isValidationError = false;

  // Code group info
  currentCodeGroup = '';
  formSchd = '';
  currentEntityName = '';
  currentPartnerName = '';
  scenario = '';
  jcdKey = 0;

  // Section header text
  sectionHeader = 'Section 4 - Foreign Taxes:';

  // Action code for loading line details (specific to this component)
  lineDetailsActionCode = 'bwlb9h';

  // Confirmation modal
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.myScreenKey = this.gtwWorkspace.workspaceFactory?.activeScreen?.screen_key ?? null;

    // No auto-load on tab switch: skip BehaviorSubject replay AND ignore
    // emissions for other screens (old CE getting destroyed). First load
    // happens only on Filter click (data-filters.refresh re-emits setTabActive).
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe((ws: any) => {
      if (!this.firstSetTabActiveSkipped) {
        this.firstSetTabActiveSkipped = true;
        return;
      }
      const incomingKey = ws?.screen_key ?? null;
      if (incomingKey && this.myScreenKey && incomingKey !== this.myScreenKey) {
        return;
      }
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      if (this.isInitialized) {
        this.tryInit();
      }
    }));

    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
      this.cdr.markForCheck();
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseURLs = { ...(this.baseURLs || {}), api: baseUrl };
    this.GENERAL_CONFIG = { ...generalConfig, base_url: baseUrl };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.scenario = this.filterParams?.scenario || '';
    this.jcdKey = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
  }

  /**
   * Gate function to prevent duplicate initialization
   */
  private tryInit(): void {
    console.log('[F1065K2-P3S4] tryInit called. isInitialized:', this.isInitialized, 'isLoading:', this.isLoading);
    
    if (this.isLoading) {
      console.log('[F1065K2-P3S4] Already initialized or loading, skipping');
      return;
    }

    // Check if ALL required data is present before initializing
    const hasGlobalService = !!this.globalService && Object.keys(this.globalService).length > 0;
    const hasBaseUrl = !!this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = !!this.filterParams && Object.keys(this.filterParams).length > 0;
    
    console.log('[F1065K2-P3S4] Data check - hasGlobalService:', hasGlobalService, 
      'hasBaseUrl:', hasBaseUrl, '(', this.GENERAL_CONFIG?.base_url, ')',
      'hasFilterParams:', hasFilterParams);

    if (!hasGlobalService || !hasBaseUrl || !hasFilterParams) {
      console.log('[F1065K2-P3S4] Missing required data, waiting...');
      return;
    }

    const contextKey = [
      this.GENERAL_CONFIG?.base_url || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.gtwWorkspace.workspaceFactory?.activeScreen?.link || ''
    ].join('|');

    if (contextKey === this.lastContextKey) {
      return;
    }

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    console.log('[F1065K2-P3S4] All data present, initializing component!');
    this.isInitialized = true;
    this.init();
  }

  /**
   * Main initialization
   */
  private init(): void {
    console.log('[F1065K2-P3S4] init() started');
    try {
      // Set up basic parameters
      this.scenario = this.filterParams.scenario || '';
      this.jcdKey = this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
      console.log('[F1065K2-P3S4] scenario:', this.scenario, 'jcdKey:', this.jcdKey);

      // Check lock status
      this.checkLockStatus();

      // Set current code group and load data
      this.setCurrentCodeGroup();
      console.log('[F1065K2-P3S4] currentCodeGroup:', this.currentCodeGroup);
      this.loadColumnHeaders();
    } catch (e) {
      console.error('[F1065K2-P3S4] Error in init:', e);
      this.loadingData = false;
      this.showLoader = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Reload data when filter params change (after initial load)
   */
  private reloadData(): void {
    console.log('[F1065K2-P3S4] reloadData() called - filter params changed');
    
    // Reset loading flags to allow reload
    this.isLoading = false;
    
    // Reset data
    this.lineData = [];
    this.groupedFormLines = [];
    this.colHeaderData = [];
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    
    // Show loading state
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();

    // Update parameters
    this.scenario = this.filterParams.scenario || '';
    this.jcdKey = this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
    
    // Recalculate code group (in case form/schedule changed)
    this.setCurrentCodeGroup();
    console.log('[F1065K2-P3S4] reloadData - currentCodeGroup:', this.currentCodeGroup);
    
    // Check lock status
    this.checkLockStatus();
    
    // Reload data
    this.loadColumnHeaders();
  }

  /**
   * Set current code group based on filter parameters
   * Pattern: {form}_{schedule}_{part}_{section} e.g., F1065_K2_P3_S4
   */
  private setCurrentCodeGroup(): void {
    const form = this.filterParams.p_form || 'F1065';
    const schedule = this.filterParams.p_schedule || 'K2';
    
    // Extract the link part (e.g., _P3_S4 from the screen link)
    // In AngularJS this comes from workspaceFactory.activeScreen.link
    // For now, we hardcode it for P3S4 component
    const linkPart = '_P3_S4';
    
    this.currentCodeGroup = `${form}_${schedule}${linkPart}`;
    this.formSchd = `${form}_${schedule}`;
  }

  /**
   * Check if tax return is locked
   */
  private checkLockStatus(): void {
    if (!this.GENERAL_CONFIG?.base_url) return;

    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        if (data && data.length > 0 && data[0].LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        }
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  // ============================================
  // DATA LOADING
  // ============================================

  /**
   * Load column headers
   */
  private loadColumnHeaders(): void {
    console.log('[F1065K2-P3S4] loadColumnHeaders() called, isLoading:', this.isLoading);
    if (this.isLoading) return;
    this.isLoading = true;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;

    const apiUrl = this.GENERAL_CONFIG.base_url;
    const taxYear = this.filterParams.tax_year || '';
    console.log('[F1065K2-P3S4] Calling getColumnHeaders with:', { apiUrl, taxYear, codeGroup: this.currentCodeGroup });

    this.service.getColumnHeaders(
      apiUrl,
      taxYear,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        console.log('[F1065K2-P3S4] getColumnHeaders response:', data);
        this.colHeaderData = data || [];
        
        if (!this.colHeaderData || this.colHeaderData.length === 0) {
          console.log('[F1065K2-P3S4] No column header data returned');
          this.service.showWarning('Data Error: No results were returned');
          this.loadingData = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }
        console.log('[F1065K2-P3S4] Got', this.colHeaderData.length, 'column headers');

        // Process column headers and load dropdown data if needed
        let hasDropdowns = false;
        
        for (const col of this.colHeaderData) {
          this.setValidationInfo(col, col);
          
          if (col.DROP_DOWN_ACTION && col.DROP_DOWN_ACTION.trim() !== '') {
            hasDropdowns = true;
            this.totalDropDownActions++;
            this.loadDropdownData(col);
          }
        }

        // If no dropdowns, proceed to load line details
        if (!hasDropdowns) {
          this.loadLineDetails();
        }
      },
      (error) => {
        console.error('Error loading column headers:', error);
        this.service.showError('Error loading form configuration');
        this.loadingData = false;
        this.showLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Load dropdown data for a column
   */
  private loadDropdownData(colData: ColumnHeaderData): void {
    const actionDetails = colData.DROP_DOWN_ACTION!.split(':');
    const actionCode = actionDetails[0];
    const codeGroup = actionDetails[1] || '';

    this.service.getDropdownData(
      this.GENERAL_CONFIG.base_url,
      actionCode,
      codeGroup,
      this.filterParams.tax_year || '',
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        colData.actionData = data || [];
        this.loadedActionCounter++;

        // When all dropdowns are loaded, load line details
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading dropdown data:', error);
        this.loadedActionCounter++;
        
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Load line details
   */
  private loadLineDetails(): void {
    console.log('[F1065K2-P3S4] loadLineDetails() called');
    this.showLoader = true;

    // Validate required filters
    // p_filer_key can be a number OR a comma-separated string of numbers
    console.log('[F1065K2-P3S4] p_filer_key:', this.filterParams.p_filer_key, 'type:', typeof this.filterParams.p_filer_key);
    const filerKey = this.filterParams.p_filer_key;
    const hasFilerKey = filerKey !== undefined && filerKey !== null && filerKey !== '' && 
                        (typeof filerKey === 'string' ? filerKey.trim().length > 0 : filerKey >= 0);
    
    if (!hasFilerKey) {
      console.log('[F1065K2-P3S4] Missing p_filer_key, showing error');
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // For K3 schedules, partner key is also required
    const partnerKey = this.filterParams.p_filer_partner_key;
    const hasPartnerKey = partnerKey !== undefined && partnerKey !== null && partnerKey !== '' &&
                          (typeof partnerKey === 'string' ? partnerKey.trim().length > 0 : (partnerKey as number) >= 0);
    
    if (this.currentCodeGroup.includes('_K3') && !hasPartnerKey) {
      console.log('[F1065K2-P3S4] Missing p_filer_partner_key for K3, showing error');
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Set entity and partner labels for confirmation modal
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (this.currentCodeGroup.includes('_K3')) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }

    console.log('[F1065K2-P3S4] Calling getLineDetails with p_filer_key:', filerKey);
    this.service.getLineDetails(
      this.GENERAL_CONFIG.base_url,
      this.lineDetailsActionCode,
      this.filterParams.tax_year || '',
      filerKey!,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams,
      this.filterParams.p_filer_partner_key
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        console.log('[F1065K2-P3S4] getLineDetails response:', data?.length, 'rows');
        console.log('[F1065K2-P3S4] Raw line data sample:', data?.slice(0, 3));
        this.lineData = data || [];
        this.groupedFormLines = this.processData(this.lineData);
        console.log('[F1065K2-P3S4] Processed', this.groupedFormLines.length, 'form lines');
        console.log('[F1065K2-P3S4] groupedFormLines sample:', this.groupedFormLines?.slice(0, 2));
        console.log('[F1065K2-P3S4] Lines with IS_DISPLAY=Y:', this.groupedFormLines?.filter(l => l.IS_DISPLAY === 'Y').length);

        // Calculate totals on form load
        for (const line of this.groupedFormLines) {
          this.calcTotalsCol(line.items);
        }

        this.showLoader = false;
        this.loadingData = false;
        this.showErrCol = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error loading line details:', error);
        this.service.showError('Error loading form data');
        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ============================================
  // DATA PROCESSING
  // ============================================

  /**
   * Process raw line data into grouped structure
   */
  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (!formLines || formLines.length === 0) {
      return [];
    }

    // Enable admin by default (following AngularJS pattern)
    this.isAdmin = true;

    return _.reduce(
      formLines,
      (memo: GroupedFormLine[], property: LineData) => {
        // Find existing bucket for this line
        let bucket = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESC: property.LINE_DESC,
          LINE_ORDER: property.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: property.SNO,
          IS_LINE_MULTI: property.IS_LINE_MULTI
        });

        // Create new bucket if needed
        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            LINE_KEY: property.LINE_KEY,
            LINE_NO: property.LINE_NO,
            LINE_DESC: property.LINE_DESC,
            LINE_ORDER: property.LINE_ORDER,
            IS_DISPLAY: 'Y',
            SNO: property.SNO,
            IS_LINE_MULTI: property.IS_LINE_MULTI,
            items: []
          };
          memo.push(bucket);
        }

        // Create item attributes
        const attribs: FormLineItem = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          COL_KEY: property.COL_KEY,
          COL_DESC: property.COL_DESC,
          COL_ORDER: property.COL_ORDER,
          IS_ENABLED: property.EDITABLE_Y_N,
          SNO: property.SNO,
          ADJ_KEY: property.ADJ_KEY,
          IS_COL_MULTI: property.IS_COL_MULTI,
          UI_COMP: property.UI_COMP,
          ACTION_Y_N: property.ACTION_Y_N,
          IS_MANDATORY: 'N',
          CELL_VALUE: property.CELL_VALUE
        };

        // Assign dropdown data if available
        if (attribs.ACTION_Y_N === 'Y') {
          const colObj = this.colHeaderData.find(col => col.COL_KEY === attribs.COL_KEY);
          if (colObj) {
            attribs.actionData = colObj.actionData;
          }
        }

        // Set validation info
        this.setValidationInfo(property, attribs);

        // Set operation type
        if (attribs.ADJ_KEY !== undefined && attribs.ADJ_KEY !== null) {
          attribs.OPERATION_TYPE = 'U';
        } else {
          attribs.OPERATION_TYPE = undefined;
        }

        // Set default values for multi-line items
        if (property.IS_LINE_MULTI === 'Y' && this.isEmptyVal(attribs.CELL_VALUE)) {
          this.setUICompDefaults(attribs);
        }

        // Convert date values
        if (attribs.UI_COMP === 'DATE' && this.isUICompNotEmpty(attribs)) {
          // Use date-utils to parse date safely (avoid timezone issues)
          const parsedDate = parseAsLocalDate(property.CELL_VALUE);
          if (parsedDate) {
            // Convert to YYYY-MM-DD string for input[type="date"]
            attribs.CELL_VALUE = this.formatDateForInput(parsedDate);
          }
        }

        // Skip from saving if disabled
        if (this.isSkipFromSaving(bucket, attribs)) {
          attribs.OPERATION_TYPE = 'N';
        }

        bucket.items.push(attribs);
        return memo;
      },
      []
    );
  }

  /**
   * Set validation info from column rules
   */
  private setValidationInfo(source: any, dest: any): void {
    if (source.VALIDATION_RULE) {
      const rules = source.VALIDATION_RULE.split(';');
      for (const rule of rules) {
        if (rule === 'REQ') {
          dest.IS_MANDATORY = 'Y';
        } else if (rule.includes('MAX_LEN:')) {
          dest.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  /**
   * Set default values based on UI component type
   */
  private setUICompDefaults(attrib: FormLineItem): void {
    if (attrib.UI_COMP === 'NUMERIC') {
      attrib.CELL_VALUE = null;
    } else if (attrib.UI_COMP === 'TEXT' || attrib.UI_COMP === 'DATE' || 
               attrib.UI_COMP === 'CHK_BX' || attrib.UI_COMP === 'DROP_DOWN') {
      attrib.CELL_VALUE = '';
    }
  }

  /**
   * Check if item should skip from saving
   */
  private isSkipFromSaving(bucket: GroupedFormLine, attribs: FormLineItem): boolean {
    return attribs.IS_ENABLED === 'N';
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  private isEmptyVal(val: any): boolean {
    return val === null || val === '' || val === undefined;
  }

  private isUICompEmpty(attrib: FormLineItem): boolean {
    if (attrib.CELL_VALUE === undefined || attrib.CELL_VALUE === null) {
      return true;
    }
    if (attrib.UI_COMP === 'NUMERIC' && attrib.CELL_VALUE === null) {
      return true;
    }
    if ((attrib.UI_COMP === 'TEXT' || attrib.UI_COMP === 'DATE' || 
         attrib.UI_COMP === 'CHK_BX' || attrib.UI_COMP === 'DROP_DOWN') && 
        attrib.CELL_VALUE === '') {
      return true;
    }
    return false;
  }

  private isUICompNotEmpty(attrib: FormLineItem): boolean {
    return !this.isUICompEmpty(attrib);
  }

  /**
   * Format date for input[type="date"] (YYYY-MM-DD)
   */
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date for save - YYYY-M-D format (without leading zeros)
   * Matches original AngularJS: tempDate.getFullYear() + '-' + (tempDate.getMonth() + 1) + '-' + tempDate.getDate()
   * Handles both Date objects and string inputs
   */
  private formatDateForSave(dateValue: string | Date | null): string {
    if (!dateValue) return '';
    
    // If it's already a Date object, format directly
    if (dateValue instanceof Date) {
      return `${dateValue.getFullYear()}-${dateValue.getMonth() + 1}-${dateValue.getDate()}`;
    }
    
    // If it's a string, parse and format
    const date = parseAsLocalDate(dateValue);
    if (date) {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }
    
    return String(dateValue);
  }

  /**
   * Parse date from input[type="date"] (YYYY-MM-DD)
   */
  private parseDateFromInput(dateString: string): Date | null {
    return parseAsLocalDate(dateString);
  }

  // ============================================
  // ROW OPERATIONS
  // ============================================

  /**
   * Add a new row (copy of selected row)
   */
  addRow(selectedRowData: GroupedFormLine): void {
    const index = this.groupedFormLines.indexOf(selectedRowData);
    const tempObj = _.cloneDeep(selectedRowData);
    
    // Find max SNO for this LINE_KEY
    const multiRow = this.groupedFormLines.filter(
      line => line.LINE_KEY === selectedRowData.LINE_KEY && line.IS_DISPLAY === 'Y'
    );
    const sno = multiRow[multiRow.length - 1].SNO + 1;

    // Reset row properties
    tempObj.LINE_DESC = '';
    tempObj.LINE_NO = '';
    tempObj.ERR_MSG = undefined;
    tempObj.SNO = sno;

    // Reset items
    for (const item of tempObj.items) {
      item.OPERATION_TYPE = 'I';
      item.ADJ_KEY = null;
      this.setUICompDefaults(item);
      item.SNO = sno;
    }

    // Insert new row
    this.groupedFormLines.splice(index + sno + 1, 0, tempObj);
    this.cdr.detectChanges();
  }

  /**
   * Delete a row
   */
  deleteRow(selectedRowData: GroupedFormLine): void {
    let isRowSaved = false;

    for (const item of selectedRowData.items) {
      if (item.OPERATION_TYPE === 'U') {
        item.OPERATION_TYPE = 'D';
        selectedRowData.IS_DISPLAY = 'N';
        isRowSaved = true;
      }
    }

    if (!isRowSaved) {
      const index = this.groupedFormLines.indexOf(selectedRowData);
      this.groupedFormLines.splice(index, 1);
    }

    this.cdr.detectChanges();
  }

  // ============================================
  // CALCULATIONS
  // ============================================

  /**
   * Calculate totals for a column (last item is the Total)
   * Matches original AngularJS: only calculate if last column IS_ENABLED === 'Y'
   * Sets null if no values are entered (not 0)
   */
  calcTotalsCol(columnValues: FormLineItem[]): void {
    if (!columnValues || columnValues.length === 0) return;

    const lastColumn = columnValues[columnValues.length - 1];
    
    // Only calculate if last column (Total) is enabled
    if (lastColumn.IS_ENABLED !== 'Y') return;

    let totalValue = 0;
    let isAnyValue = false;

    for (let i = 0; i < columnValues.length - 1; i++) {
      const item = columnValues[i];
      if (item.UI_COMP === 'NUMERIC' && 
          item.CELL_VALUE !== null && item.CELL_VALUE !== undefined && item.CELL_VALUE !== '') {
        totalValue += Number(item.CELL_VALUE) || 0;
        isAnyValue = true;
      }
    }

    // Set total only if any value was entered, otherwise null
    if (isAnyValue) {
      lastColumn.CELL_VALUE = totalValue;
    } else {
      lastColumn.CELL_VALUE = null;
    }
  }

  /**
   * Handle numeric input change - trigger total recalculation
   */
  onNumericChange(items: FormLineItem[]): void {
    this.calcTotalsCol(items);
    this.cdr.detectChanges();
  }

  /**
   * Handle date input change - uses parseAsLocalDate to avoid timezone shift
   */
  onDateChange(item: FormLineItem, dateString: string): void {
    if (dateString) {
      item.CELL_VALUE = parseAsLocalDate(dateString);
    } else {
      item.CELL_VALUE = '';
    }
    this.cdr.detectChanges();
  }

  /**
   * Check if item is the last NUMERIC column (Total column - should be readonly)
   * The Total column auto-calculates from other NUMERIC fields
   */
  isLastNumericColumn(items: FormLineItem[], item: FormLineItem): boolean {
    if (!items || items.length === 0) return false;
    
    // Find all NUMERIC items
    const numericItems = items.filter(i => i.UI_COMP === 'NUMERIC');
    if (numericItems.length === 0) return false;
    
    // Check if this item is the last NUMERIC item
    const lastNumeric = numericItems[numericItems.length - 1];
    return item.COL_KEY === lastNumeric.COL_KEY;
  }

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   * Matches original AngularJS only-digits directive behavior
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Block all other characters (including decimal point)
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: FormLineItem): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    // Remove all non-digit characters except minus at start
    let filtered = pastedText.replace(/[^0-9-]/g, '');
    // Ensure minus is only at start
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    item.CELL_VALUE = filtered ? parseInt(filtered, 10) : null;
    this.cdr.detectChanges();
  }

  // ============================================
  // VALIDATION
  // ============================================

  /**
   * Validate the form
   */
  private validateForm(): boolean {
    this.isValidationError = false;
    let requiredMsgAlreadyAdded = false;

    for (const line of this.groupedFormLines) {
      line.ERR_MSG = undefined;
      requiredMsgAlreadyAdded = false;

      for (const item of line.items) {
        // Reset error flag
        item.IS_ERROR = 'N';

        if (line.IS_DISPLAY === 'Y' && item.IS_ENABLED !== 'N') {
          // Mandatory validation
          if (item.IS_MANDATORY === 'Y' && 
              this.isUICompEmpty(item) && 
              this.isAnyOtherValueEntered(item, line.items)) {
            this.setErrorAttribs(line, item, 'Mandatory field(s) are missing', requiredMsgAlreadyAdded);
            requiredMsgAlreadyAdded = true;
          }
        }
      }
    }

    if (this.isValidationError) {
      this.showErrCol = true;
    }

    this.cdr.detectChanges();
    return !this.isValidationError;
  }

  /**
   * Check if any other value in the row is entered
   */
  private isAnyOtherValueEntered(item: FormLineItem, itemList: FormLineItem[]): boolean {
    return itemList.some(x => x !== item && x.IS_ENABLED === 'Y' && this.isUICompNotEmpty(x));
  }

  /**
   * Set error attributes on a line/item
   */
  private setErrorAttribs(line: GroupedFormLine, col: FormLineItem, errMsg: string, alreadyAdded: boolean): void {
    if (!line.ERR_MSG) {
      line.ERR_MSG = '<ol></ol>';
    }
    col.IS_ERROR = 'Y';
    this.isValidationError = true;
    
    if (!alreadyAdded) {
      line.ERR_MSG = line.ERR_MSG.replace('</ol>', `<li>${errMsg}</li></ol>`);
    }
  }

  // ============================================
  // SAVE & RESET
  // ============================================

  /**
   * Reset the form
   */
  reset(): void {
    // Clear array before reload to prevent rendering stale data during async fetch
    this.groupedFormLines = [];
    this.showLoader = true;
    this.cdr.detectChanges();
    
    this.isInitialized = false;
    this.isLoading = false;
    this.loadColumnHeaders();
  }

  /**
   * Show save confirmation dialog
   */
  save(): void {
    if (this.isSaveClicked) {
      this.service.showInfo('Please wait while we save this request');
      return;
    }

    // Validate form
    if (!this.validateForm()) {
      this.service.showError('Please fix all error(s)');
      return;
    }

    // Update confirmation message with entity name
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure to save data for: ${this.currentEntityName}${this.currentPartnerName ? ' / ' + this.currentPartnerName : ''}`
    };
    
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  /**
   * Handle save confirmation
   */
  onSaveConfirm(): void {
    this.showConfirmModal = false;
    this.performSave();
  }

  /**
   * Handle save cancellation
   */
  onSaveCancel(): void {
    this.showConfirmModal = false;
    this.isSaveClicked = false;
    this.cdr.detectChanges();
  }

  /**
   * Perform the actual save operation
   */
  private performSave(): void {
    this.isSaveClicked = true;

    // Build settings object - only 3 fields, matching original
    const clobSettings: ClobSettingsObj = {
      jcd_key: this.jcdKey,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    // Build data to save
    const dataToSave = this.buildDataToSave();

    // First check lock status
    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || '',
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (lockData) => {
        // Proceed with save (following AngularJS pattern - lock check is informational)
        this.service.saveData(
          this.GENERAL_CONFIG.base_url,
          clobSettings,
          dataToSave,
          this.globalService?.globalParams || {},
          this.filterParams,
          this.globalService?.inputs?.tax_year?.values || []
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (result) => {
            // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
            if (result.callSuccess != 1) {
              // Handle errorMessage: "null" string from API
              const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                ? result.errorMessage 
                : 'Error saving data';
              this.service.showError(errorMsg);
            } else {
              this.service.showSuccess('Data have been saved/updated');
              this.requestGridUpdate.emit('refresh');
            }
            this.isSaveClicked = false;
            this.showErrCol = false;
            this.cdr.detectChanges();
          },
          (error) => {
            console.error('Error saving data:', error);
            this.service.showError('Error occurred while saving. Please try again.');
            this.isSaveClicked = false;
            this.cdr.detectChanges();
          }
        );
      },
      (error) => {
        console.error('Error checking lock status:', error);
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Build data array for save - matching original AngularJS pushToDataToSave logic
   */
  private buildDataToSave(): SaveDataItem[] {
    const dataToSave: SaveDataItem[] = [];

    for (const line of this.groupedFormLines) {
      for (const item of line.items) {
        const returnObj: SaveDataItem = {
          SCHD_KEY: item.SCHD_KEY,
          LINE_KEY: item.LINE_KEY,
          COL_KEY: item.COL_KEY,
          SNO: item.SNO,
          OPERATION_TYPE: null as any
        };

        // Always include ADJ_KEY (matching original AngularJS behavior)
        returnObj.ADJ_KEY = item.ADJ_KEY;

        // Handle cell value based on component type (only if IS_ENABLED != 'N')
        if (item.IS_ENABLED !== 'N') {
          if (item.UI_COMP === 'DATE') {
            if (this.isUICompEmpty(item)) {
              returnObj.CELL_VALUE = '';
            } else {
              // Format date as YYYY-M-D (without leading zeros) to match original AngularJS
              returnObj.CELL_VALUE = this.formatDateForSave(item.CELL_VALUE);
            }
          } else if (item.UI_COMP === 'NUMERIC') {
            // Convert to number for NUMERIC fields, empty values should be null
            if (item.CELL_VALUE !== null && item.CELL_VALUE !== undefined && item.CELL_VALUE !== '') {
              returnObj.CELL_VALUE = Number(item.CELL_VALUE);
            } else {
              returnObj.CELL_VALUE = null; // Empty NUMERIC = null (matching original)
            }
          } else if (item.UI_COMP === 'CHK_BX') {
            // Checkboxes: true → "X", false/empty → "" (matching original AngularJS)
            returnObj.CELL_VALUE = item.CELL_VALUE === true || item.CELL_VALUE === 'X' ? 'X' : '';
          } else if (item.UI_COMP === 'DROP_DOWN') {
            // Dropdowns: empty value should be null (matching original AngularJS)
            if (item.CELL_VALUE === null || item.CELL_VALUE === undefined || item.CELL_VALUE === '') {
              returnObj.CELL_VALUE = null;
            } else {
              returnObj.CELL_VALUE = item.CELL_VALUE;
            }
          } else {
            returnObj.CELL_VALUE = item.CELL_VALUE;
          }
        }

        // Determine operation type - matching original AngularJS logic exactly
        // In original, empty NUMERIC fields have null value, not empty string
        const hasValue = item.CELL_VALUE != null && item.CELL_VALUE !== '' && item.CELL_VALUE !== undefined;
        
        // FIX: Match original AngularJS pattern (P7S2, P12, P11 controllers)
        // Set OPERATION_TYPE UNCONDITIONALLY first, then handle special cases
        // This ensures items with cleared values (OPERATION_TYPE='U' but empty value) are still sent to API
        returnObj.OPERATION_TYPE = item.OPERATION_TYPE || null;

        if (line.IS_LINE_MULTI === 'Y') {
          if (!item.OPERATION_TYPE) {
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'N') {
            returnObj.OPERATION_TYPE = null;
          }
        } else {
          if (item.OPERATION_TYPE == null && hasValue) {
            // New value being inserted
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            // After insert, mark as update for future saves
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'D') {
            // Deletion
            returnObj.OPERATION_TYPE = 'D';
            item.OPERATION_TYPE = undefined;
          } else if (item.OPERATION_TYPE === 'N') {
            // Disabled/not saveable
            returnObj.OPERATION_TYPE = null;
          }
          // Note: When OPERATION_TYPE is 'U', it stays 'U' (set unconditionally above)
          // This allows clearing values to be sent to API
        }

        // Only add if OPERATION_TYPE is not null (matching original: if(returnObj.OPERATION_TYPE!=null))
        if (returnObj.OPERATION_TYPE != null) {
          dataToSave.push(returnObj);
        }
      }
    }

    return dataToSave;
  }

  // ============================================
  // TRACK BY FUNCTIONS
  // ============================================

  trackByLine(index: number, line: GroupedFormLine): string {
    return `${line.LINE_KEY}_${line.SNO}_${line.IS_DISPLAY}`;
  }

  trackByItem(index: number, item: FormLineItem): string {
    return `${item.LINE_KEY}_${item.COL_KEY}_${item.SNO}`;
  }

  trackByCol(index: number, col: ColumnHeaderData): number {
    return col.COL_KEY;
  }
}
